import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen, Settings, Plus, Trash2, FileText, Video, ChevronDown, ChevronUp, File, FolderOpen, PanelRight, Link, Layout, HelpCircle, Edit3, X, Zap, Loader2, Menu, Clock, Activity, BarChart3, Download, Calendar, Filter
} from 'lucide-react';
import { useAuditLog } from '../hooks/useAuditLog';

// --- CONFIGURATION AND UTILS ---
const Poppins = "font-poppins";
const Inter = "font-inter";
const uniqueId = () => Math.random().toString(36).substring(2, 9);

const STORAGE_KEY = 'enterprise_portal_data';

// Initial state for a new course
const INITIAL_COURSE_STATE = {
  id: 'course-root',
  title: 'Enterprise Training Portal',
  description: 'Manage comprehensive training modules for the organization.',
  category: 'HR & Compliance',
  status: 'Draft',
};

// --- LOCAL STORAGE HOOK ---
const useLocalCourseState = (initialModules) => {
  const [courseData, setCourseData] = useState(INITIAL_COURSE_STATE);
  const [modules, setModules] = useState(initialModules);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        setCourseData(parsedState.details || INITIAL_COURSE_STATE);
        setModules(parsedState.modules || []);
      } catch (e) {
        console.error("Could not parse saved state from local storage:", e);
      }
    }
    setIsDataLoaded(true);
  }, []);

  // Save state to localStorage whenever courseData or modules changes
  useEffect(() => {
    if (isDataLoaded) {
      const stateToSave = JSON.stringify({ details: courseData, modules });
      localStorage.setItem(STORAGE_KEY, stateToSave);
      // console.log("State saved to local storage.");
    }
  }, [courseData, modules, isDataLoaded]);

  const updateCourse = useCallback((detailsUpdate) => {
    setCourseData(prev => ({ ...prev, ...detailsUpdate }));
  }, []);

  const updateModules = useCallback((newModules) => {
    setModules(newModules);
  }, []);

  return { courseData, modules, updateCourse, updateModules, isDataLoaded };
};


// --- HELPERS (INPUTS & CARDS) ---

const Card = ({ title, children, icon: Icon, className = '' }) => (
    <div className={`bg-white p-5 rounded-xl shadow-lg border border-gray-100 mb-6 ${className}`}>
        {title && (
            <h4 className={`text-lg font-semibold text-gray-800 border-b pb-2 mb-4 flex items-center ${Poppins}`}>
                {Icon && <Icon size={20} className="mr-2 text-indigo-500" />}
                {title}
            </h4>
        )}
        {children}
    </div>
);

// Unified Input Field (Auto-saves on blur)
const InputField = ({ label, id, value, onChange, onSave, type = 'text', required = false, isTextArea = false, placeholder = '' }) => {
    const baseClasses = `mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-150 ${Inter}`;
    return (
        <div className="mb-4">
            <label htmlFor={id} className={`block text-xs font-semibold text-gray-500 uppercase mb-1 ${Inter}`}>
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {isTextArea ? (
                <textarea
                    id={id}
                    name={id}
                    rows="4"
                    value={value || ''}
                    onChange={onChange}
                    onBlur={onSave}
                    placeholder={placeholder}
                    required={required}
                    className={baseClasses}
                ></textarea>
            ) : (
                <input
                    id={id}
                    name={id}
                    type={type}
                    value={value || ''}
                    onChange={onChange}
                    onBlur={onSave}
                    required={required}
                    placeholder={placeholder}
                    className={baseClasses}
                />
            )}
        </div>
    );
};

// Unified Select Field (Auto-saves on blur)
const SelectField = ({ label, id, value, onChange, onSave, options }) => (
    <div className="mb-4">
        <label htmlFor={id} className={`block text-xs font-semibold text-gray-500 uppercase mb-1 ${Inter}`}>
            {label}
        </label>
        <select
            id={id}
            name={id}
            value={value || options[0]}
            onChange={onChange}
            onBlur={onSave}
            className={`mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-150 ${Inter}`}
        >
            {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
            ))}
        </select>
    </div>
);


// --- 3. DETAIL CONFIGURATION PANEL (Right Main Area) ---
const DetailConfiguration = ({ modules, selectedId, selectedType, selectedModuleId, courseData, updateCourse, updateModules, onSelectElement }) => {
  const [details, setDetails] = useState(null);
  const currentModule = selectedModuleId ? modules.find(m => m.id === selectedModuleId) : null;

  // Sync state with selected element
  useEffect(() => {
    let newDetails = null;
    if (selectedType === 'course') {
      newDetails = courseData;
    } else if (selectedType === 'module' && selectedId) {
      newDetails = modules.find(m => m.id === selectedId);
    } else if (selectedType === 'lesson' && selectedId && selectedModuleId) {
      newDetails = currentModule?.lessons.find(l => l.id === selectedId);
    }
    setDetails(newDetails);
  }, [selectedId, selectedType, selectedModuleId, modules, courseData, currentModule]);

  // Unified Auto-Save Logic (Zoho/Keka style: changes save instantly)
  const handleSave = () => {
    if (!details) return;

    if (selectedType === 'course') {
      updateCourse(details);
    } else if (selectedType === 'module') {
      const updatedModules = modules.map(m => (m.id === selectedId ? details : m));
      updateModules(updatedModules);
    } else if (selectedType === 'lesson') {
      const updatedModules = modules.map(mod =>
        mod.id === selectedModuleId
          ? {
              ...mod,
              lessons: mod.lessons.map(l => (l.id === selectedId ? details : l)),
            }
          : mod
      );
      updateModules(updatedModules);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Update local details state immediately for responsiveness
    setDetails(prev => ({ ...prev, [name]: value }));
  };
  
  const handleDelete = () => {
    if (!details) return;
    
    if (selectedType === 'module') {
        const updatedModules = modules.filter(m => m.id !== selectedId);
        updateModules(updatedModules);
        onSelectElement(INITIAL_COURSE_STATE.id, 'course');
    } else if (selectedType === 'lesson' && selectedModuleId) {
        const updatedModules = modules.map(mod =>
            mod.id === selectedModuleId
                ? { ...mod, lessons: mod.lessons.filter(l => l.id !== selectedId) }
                : mod
        );
        updateModules(updatedModules);
        onSelectElement(currentModule.id, 'module', selectedModuleId);
    }
  };


  // --- Render Functions ---

  if (!details) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-gray-50 rounded-xl">
        <PanelRight size={48} className="text-indigo-400 mb-4" />
        <h3 className={`text-xl font-bold ${Poppins} text-gray-700`}>Content Management Workspace</h3>
        <p className={`text-sm text-gray-500 ${Inter}`}>
          Select an item from the **Course Structure** panel on the left to configure its details.
        </p>
      </div>
    );
  }

  // --- Course Settings View ---
  const renderCourseConfig = () => (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-3xl font-extrabold text-gray-900 ${Poppins}`}>Portal Settings</h2>
        <div className="flex items-center space-x-2 text-gray-500">
            <HelpCircle size={18} />
            <span className="text-sm">Contextual Help</span>
        </div>
      </div>
      
      <Card title="Portal Identification" icon={Settings}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Portal Title"
            id="title"
            name="title"
            value={details.title}
            onChange={handleChange}
            onSave={handleSave}
            required
            placeholder="e.g., Enterprise React Development"
          />
          <SelectField
            label="Category"
            id="category"
            name="category"
            value={details.category}
            onChange={handleChange}
            onSave={handleSave}
            options={['HR & Compliance', 'IT & Security', 'Finance', 'Development']}
          />
        </div>
        <InputField
          label="Portal Description (Summary)"
          id="description"
          name="description"
          value={details.description}
          onChange={handleChange}
          onSave={handleSave}
          isTextArea
          placeholder="A brief summary of the entire training program."
        />
      </Card>

      <Card title="Deployment Status" icon={Zap}>
        <SelectField
          label="Course Status"
          id="status"
          name="status"
          value={details.status}
          onChange={handleChange}
          onSave={handleSave}
          options={['Draft', 'Review', 'Published', 'Archived']}
        />
        <p className={`text-xs text-gray-500 mt-2 ${Inter}`}>
            Status **Published** makes the course visible to all targeted employees.
        </p>
      </Card>
    </>
  );

  // --- Module Settings View ---
  const renderModuleConfig = () => (
    <>
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h2 className={`text-3xl font-extrabold text-gray-900 ${Poppins}`}>Module Configuration</h2>
        <button
            onClick={handleDelete}
            className={`flex items-center px-4 py-2 text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium border border-red-300 ${Inter}`}
        >
            <Trash2 size={16} className="mr-2" /> Delete Module
        </button>
      </div>

      <Card title="Module Identity" icon={Edit3}>
        <InputField
          label="Module Title"
          id="title"
          name="title"
          value={details.title}
          onChange={handleChange}
          onSave={handleSave}
          required
          placeholder="e.g., Setting up the Environment"
        />
        <InputField
          label="Module Goal (Internal Note)"
          id="goal"
          name="goal"
          value={details.goal}
          onChange={handleChange}
          onSave={handleSave}
          isTextArea
          placeholder="What key objective should be met?"
        />
      </Card>
    </>
  );

  // --- Lesson Settings View ---
  const renderLessonConfig = () => {
      const Icon = details.type === 'video' ? Video : details.type === 'quiz' ? FileText : File;
      const contentStatus = details.contentUrl?.trim() ? 'Ready' : 'Missing';
      const statusColor = contentStatus === 'Ready' ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50';

      return (
          <>
            <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h2 className={`text-3xl font-extrabold text-gray-900 ${Poppins}`}>Lesson Configuration</h2>
                <button
                    onClick={handleDelete}
                    className={`flex items-center px-4 py-2 text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium border border-red-300 ${Inter}`}
                >
                    <Trash2 size={16} className="mr-2" /> Delete Lesson
                </button>
            </div>

            <Card title="Lesson Details" icon={Icon}>
                <InputField
                    label="Lesson Title"
                    id="title"
                    name="title"
                    value={details.title}
                    onChange={handleChange}
                    onSave={handleSave}
                    required
                    placeholder="e.g., Understanding React Components"
                />
                
                <div className="grid grid-cols-2 gap-4">
                    <SelectField
                        label="Content Type"
                        id="type"
                        name="type"
                        value={details.type}
                        onChange={handleChange}
                        onSave={handleSave}
                        options={['video', 'article', 'quiz', 'assignment']}
                    />
                    <div className="flex flex-col justify-end pb-1">
                        <label className={`block text-xs font-semibold text-gray-500 uppercase mb-1 ${Inter}`}>
                            Content Link Status
                        </label>
                        <div className={`text-sm font-bold px-3 py-2 rounded-lg text-center border ${statusColor} ${Inter}`}>
                            {contentStatus}
                        </div>
                    </div>
                </div>
            </Card>

            <Card title="Content Source" icon={Link}>
                <InputField
                    label="Content URL / File Link"
                    id="contentUrl"
                    name="contentUrl"
                    value={details.contentUrl}
                    onChange={handleChange}
                    onSave={handleSave}
                    placeholder="https://content.cdn/lesson-video.mp4"
                />
            </Card>

            {/* Simulated Rich Content Area (Zoho-style detail card) */}
            <Card title="Inline Editor" icon={Layout}>
                <div className="p-6 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-center text-gray-500 italic h-32 flex items-center justify-center">
                    <p className={`text-sm ${Inter}`}>
                        This area would integrate a full rich-text editor (like TinyMCE or Quill) for inline article content.
                    </p>
                </div>
            </Card>
          </>
      );
  };

  switch (selectedType) {
    case 'course':
      return renderCourseConfig();
    case 'module':
      return renderModuleConfig();
    case 'lesson':
      return renderLessonConfig();
    default:
      return null;
  }
};


// --- 2. MODULE ITEM COMPONENT (Fixes rules-of-hooks error) ---
const ModuleItem = ({ module, index, selectedId, onSelectElement, addLesson }) => {
    // FIX: useState is now correctly inside a Function Component
    const [isExpanded, setIsExpanded] = useState(true);
    const isModuleSelected = module.id === selectedId;

    return (
        <div key={module.id} className="mt-1">
            {/* Module Header (Clickable for config) */}
            <div 
            className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-150 ${
                isModuleSelected 
                ? 'bg-indigo-100 text-indigo-800 font-semibold' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => onSelectElement(module.id, 'module')}
            >
            <button 
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} 
                className="p-1 text-gray-500 hover:text-indigo-600 mr-1"
            >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
            <FolderOpen size={16} className={`mr-2 ${isModuleSelected ? 'text-indigo-600' : 'text-gray-500'}`} />
            <span className={`text-sm flex-grow truncate ${Inter}`}>
                {index + 1}. {module.title}
            </span>
            </div>
            
            {/* Lessons List (Nested) */}
            {isExpanded && (
            <div className="ml-5 border-l border-gray-200 pl-3 py-1 space-y-1">
                {module.lessons.map((lesson, lessonIndex) => {
                const Icon = lesson.type === 'video' ? Video : lesson.type === 'quiz' ? FileText : File;
                const isSelected = lesson.id === selectedId;

                return (
                    <div 
                    key={lesson.id} 
                    className={`flex items-center py-1.5 px-2 rounded-md cursor-pointer transition-all duration-150 ${
                        isSelected ? 'bg-indigo-50 border-l-2 border-indigo-500 text-indigo-700 font-medium' : 'hover:bg-gray-50 text-gray-600'
                    }`}
                    onClick={() => onSelectElement(lesson.id, 'lesson', module.id)}
                    >
                    <Icon size={12} className="mr-2" />
                    <span className={`text-xs flex-grow truncate ${Inter}`}>
                        {lessonIndex + 1}. {lesson.title}
                    </span>
                    </div>
                );
                })}
                
                {/* Add Lesson Button */}
                <button
                onClick={(e) => { e.stopPropagation(); addLesson(module.id); }}
                className={`w-full flex items-center justify-center text-xs font-medium text-indigo-500 mt-2 py-1 hover:bg-indigo-50 rounded-md transition-colors border-dashed border border-indigo-200 ${Inter}`}
                >
                <Plus size={12} className="mr-1" /> Add Lesson
                </button>
            </div>
            )}
        </div>
    );
};


// --- 2. HIERARCHY TREE (Left Sidebar) ---
const HierarchyTree = ({ courseData, modules, updateModules, selectedId, onSelectElement }) => {
  
  // --- Module and Lesson manipulation logic ---
  const addModule = () => {
    const newModule = { id: uniqueId(), title: `Module ${modules.length + 1}`, goal: '', lessons: [] };
    updateModules([...modules, newModule]);
    onSelectElement(newModule.id, 'module');
  };

  const addLesson = (moduleId) => {
    const newLesson = { id: uniqueId(), title: `New Lesson`, type: 'article', contentUrl: '' };
    const updatedModules = modules.map((mod) =>
      mod.id === moduleId ? { ...mod, lessons: [...mod.lessons, newLesson] } : mod
    );
    updateModules(updatedModules);
    onSelectElement(newLesson.id, 'lesson', moduleId);
  };
  
  const totalLessons = modules.reduce((acc, mod) => acc + mod.lessons.length, 0);

  return (
    <div className="p-4 space-y-4 bg-white h-full flex flex-col border-r border-gray-200 shadow-xl">
      
      {/* Sidebar Header */}
      <div className="flex justify-between items-center pb-2 border-b border-gray-100">
        <h3 className={`text-xl font-bold text-gray-800 ${Poppins}`}>Course Structure</h3>
        <button
          onClick={addModule}
          className={`flex items-center px-3 py-1 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition-colors ${Inter}`}
        >
          <Plus size={16} className="mr-1" /> Module
        </button>
      </div>

      {/* Course/Project Root Element */}
      <div 
        className={`flex items-center p-2.5 rounded-lg cursor-pointer transition-all duration-150 shadow-sm ${
          selectedId === courseData.id 
          ? 'bg-indigo-50 border border-indigo-300 text-indigo-800 font-bold' 
          : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-200'
        }`}
        onClick={() => onSelectElement(courseData.id, 'course')}
      >
        <BookOpen size={18} className="mr-3 text-indigo-500" />
        <span className={`text-sm flex-grow truncate ${Inter}`}>{courseData.title}</span>
        <Settings size={14} className="text-gray-400" />
      </div>

      {/* Hierarchy List */}
      <div className="flex-grow overflow-y-auto pr-1">
        {modules.map((module, index) => (
            <ModuleItem 
                key={module.id} 
                module={module} 
                index={index} 
                selectedId={selectedId}
                onSelectElement={onSelectElement}
                addLesson={addLesson}
            />
        ))}
      </div>

      <p className={`text-xs text-gray-500 pt-2 border-t border-gray-100 ${Inter}`}>
        Total: {modules.length} Modules | {totalLessons} Lessons
      </p>
    </div>
  );
};


// --- ACTIVITY LOGS BUTTON COMPONENT ---
const ActivityLogsButton = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  const [reportType, setReportType] = useState('daily');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedFormat, setSelectedFormat] = useState('excel');

  const activityLogs = [
    { id: 1, employeeId: 'EMP001', action: 'Created Module', modifiedBy: 'John Doe', date: '2024-01-15', details: 'Created Module 1: HR Portal Setup' },
    { id: 2, employeeId: 'EMP002', action: 'Updated Lesson', modifiedBy: 'Jane Smith', date: '2024-01-14', details: 'Updated User Roles & Access lesson content' },
    { id: 3, employeeId: 'EMP001', action: 'Deleted Lesson', modifiedBy: 'John Doe', date: '2024-01-13', details: 'Removed outdated compliance lesson' },
    { id: 4, employeeId: 'EMP003', action: 'Published Course', modifiedBy: 'Mike Johnson', date: '2024-01-12', details: 'Published Enterprise Training Portal' },
  ];

  const handleDownloadReport = () => {
    const fileName = `course-report-${reportType}-${new Date().toISOString().split('T')[0]}.${selectedFormat}`;
    console.log(`Downloading ${fileName}`);
    setShowModal(false);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium ${Inter}`}
      >
        <Activity size={16} className="mr-2" />
        Activity & Reports
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className={`text-xl font-semibold text-gray-800 ${Poppins}`}>Activity Logs & Reports</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-6 py-3 text-sm font-medium ${activeTab === 'activity' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'} ${Inter}`}
              >
                <Activity size={16} className="inline mr-2" />
                Activity Logs
              </button>
              <button
                onClick={() => setActiveTab('reports')}
                className={`px-6 py-3 text-sm font-medium ${activeTab === 'reports' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'} ${Inter}`}
              >
                <BarChart3 size={16} className="inline mr-2" />
                Reports
              </button>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              {activeTab === 'activity' ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${Inter}`}>Employee ID</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${Inter}`}>Action</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${Inter}`}>Modified By</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${Inter}`}>Date</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${Inter}`}>Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {activityLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className={`px-4 py-3 text-sm font-medium text-gray-900 ${Inter}`}>{log.employeeId}</td>
                          <td className={`px-4 py-3 text-sm text-gray-500 ${Inter}`}>{log.action}</td>
                          <td className={`px-4 py-3 text-sm text-gray-500 ${Inter}`}>{log.modifiedBy}</td>
                          <td className={`px-4 py-3 text-sm text-gray-500 ${Inter}`}>{log.date}</td>
                          <td className={`px-4 py-3 text-sm text-gray-500 ${Inter}`}>{log.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${Inter}`}>Report Type</label>
                      <select
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${Inter}`}
                      >
                        <option value="daily">Daily</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="half-yearly">Half Yearly</option>
                        <option value="yearly">Yearly</option>
                        <option value="custom">Custom Date Range</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${Inter}`}>Format</label>
                      <select
                        value={selectedFormat}
                        onChange={(e) => setSelectedFormat(e.target.value)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${Inter}`}
                      >
                        <option value="excel">Excel (.xlsx)</option>
                        <option value="pdf">PDF (.pdf)</option>
                      </select>
                    </div>
                    
                    <div className="flex items-end">
                      <button
                        onClick={handleDownloadReport}
                        className={`w-full flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors ${Inter}`}
                      >
                        <Download size={16} className="mr-2" />
                        Download
                      </button>
                    </div>
                  </div>
                  
                  {reportType === 'custom' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className={`block text-sm font-medium text-gray-700 mb-2 ${Inter}`}>Start Date</label>
                        <input
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${Inter}`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium text-gray-700 mb-2 ${Inter}`}>End Date</label>
                        <input
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${Inter}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// --- MAIN APP COMPONENT ---
const App = () => {
  // Initial demo data for a robust structure
  const initialModules = [
    { id: uniqueId(), title: 'Module 1: HR Portal Setup', goal: 'Configure permissions and access.', lessons: [
      { id: uniqueId(), title: 'User Roles & Access', type: 'article', contentUrl: 'https://kb.zoho.com/roles-access' },
      { id: uniqueId(), title: 'Initial Data Import', type: 'video', contentUrl: 'https://youtube.com/import-guide' },
      { id: uniqueId(), title: 'Quiz: Security Policy', type: 'quiz', contentUrl: '' },
    ]},
    { id: uniqueId(), title: 'Module 2: Payroll & Compliance', goal: 'Ensure 100% compliance knowledge.', lessons: [
      { id: uniqueId(), title: 'Tax Deduction Laws (2025)', type: 'article', contentUrl: 'https://tax-law.gov/update' },
      { id: uniqueId(), title: 'Annual Compliance Assignment', type: 'assignment', contentUrl: '' },
    ]},
  ];

  const { courseData, modules, updateCourse, updateModules, isDataLoaded } = useLocalCourseState(initialModules);
  
  const [selectedId, setSelectedId] = useState(INITIAL_COURSE_STATE.id);
  const [selectedType, setSelectedType] = useState('course');
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  
  // Audit logging hook
  const { logCreate, logUpdate, logDelete } = useAuditLog('Course Creation');

  const handleSelectElement = useCallback((id, type, moduleId = null) => {
    setSelectedId(id);
    setSelectedType(type);
    setSelectedModuleId(moduleId);
  }, []);

  // --- Contextual Header Data ---
  let elementTitle = courseData.title;
  let elementBreadcrumb = 'Portal Settings';
  if (selectedType === 'module' && selectedId) {
    const mod = modules.find(m => m.id === selectedId);
    elementTitle = mod ? mod.title : 'Module';
    elementBreadcrumb = `Course Structure / Module`;
  } else if (selectedType === 'lesson' && selectedId && selectedModuleId) {
    const mod = modules.find(m => m.id === selectedModuleId);
    const lesson = mod?.lessons.find(l => l.id === selectedId);
    elementTitle = lesson ? lesson.title : 'Lesson';
    elementBreadcrumb = `Course Structure / ${mod?.title || 'Module'} / Lesson`;
  }
  // --- End Contextual Header Data ---


  if (!isDataLoaded) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        {/* FIX: Loader2 is now imported correctly */}
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <p className={`ml-3 text-lg font-semibold ${Poppins}`}>Loading Enterprise Portal Interface...</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-100 flex items-center justify-center p-4 sm:p-8 ${Inter}`}>
      
      {/* Font Imports (Simulated for Immersive) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@600;700;800&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Main Portal Container */}
      <div className="w-full max-w-7xl h-[92vh] flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-300">
        
        {/* 1. Contextual Header (Top Bar - ZoHo/Keka Style) */}
        <header className="flex-shrink-0 flex items-center justify-between p-4 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex flex-col">
                <p className={`text-xs text-gray-500 font-medium uppercase ${Inter}`}>{elementBreadcrumb}</p>
                <h1 className={`text-2xl font-bold text-gray-900 ${Poppins} truncate max-w-lg`}>{elementTitle}</h1>
            </div>
            <div className="flex items-center space-x-3">
                <ActivityLogsButton />
                <span className={`text-sm font-medium text-gray-600 px-3 py-1 rounded-full ${courseData.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    Status: {courseData.status}
                </span>
                <button 
                    // FIX: Changed to the correctly defined function handleSelectElement
                    onClick={() => handleSelectElement(INITIAL_COURSE_STATE.id, 'course')}
                    className={`flex items-center px-4 py-2 bg-indigo-500 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-600 transition-colors ${Inter}`}
                >
                    <BookOpen size={16} className="mr-2" /> View Course Preview
                </button>
            </div>
        </header>

        {/* 2. Main Workspace Area (Split Panels) */}
        <div className="flex-grow flex flex-col overflow-hidden">
          <div className="flex-grow flex overflow-hidden">
            {/* 2. Left Panel (Hierarchy Tree) */}
            <div className="w-80 shrink-0 h-full overflow-hidden">
                <HierarchyTree 
                    courseData={courseData}
                    modules={modules} 
                    updateModules={updateModules}
                    selectedId={selectedId} 
                    onSelectElement={handleSelectElement}
                />
            </div>

            {/* 3. Right Panel (Detail Configuration) */}
            <div className="flex-grow p-6 bg-gray-50 overflow-y-auto">
                <DetailConfiguration 
                    modules={modules} 
                    selectedId={selectedId} 
                    selectedType={selectedType}
                    selectedModuleId={selectedModuleId}
                    courseData={courseData}
                    updateCourse={updateCourse}
                    updateModules={updateModules}
                    onSelectElement={handleSelectElement}
                />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;